import api from '../../utils/api';
import {
  ApiListResponse,
  ApiCampaignListItem,
  ApiAdSalesRow,
  ApiUpsizeRow,
  ApiDealRow,
  CampaignListParams,
  CreateOpportunityGroupRequest,
  PatchOpportunityGroupRequest,
  CreateOpportunityRequest,
  PatchOpportunityRequest,
  CreateDealAssetRequest,
  PatchDealAssetRequest,
  BaselineV2Response,
  ApiListMembersResponse,
  ApiMember,
  ApiEditBrandAccessBody,
  ApiEditBrandAccessResponse,
  ApiInvoicingUnit,
} from './types';

// V2 API base path. V1 uses /mission-control-service/moop; V2 adds /v2/ segment.
// The api-gateway rewrites /mission-control-service/v2/... → /v2/... on the backend.
const BASE_URL = '/mission-control-service/v2/mission-control/moop';

class CampaignManagerAPI {
  private client: ReturnType<typeof api.createMissionControlClient> | null = null;
  private getClient() {
    if (!this.client) {
      this.client = api.createMissionControlClient();
    }
    return this.client;
  }

  /** 1. GET /campaign-manager/items — unified paginated list */
  async getItems(params: CampaignListParams = {}): Promise<ApiListResponse<ApiCampaignListItem>> {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.types?.length) query.set('types', params.types.join(','));
    if (params.statuses?.length) query.set('statuses', params.statuses.join(','));
    if (params.merchantIds?.length) query.set('merchantIds', params.merchantIds.join(','));
    if (params.packageTiers?.length) query.set('packageTiers', params.packageTiers.join(','));
    if (params.countries?.length) query.set('countries', params.countries.join(','));
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.sort) query.set('sort', params.sort);
    if (params.offset !== null && params.offset !== undefined) query.set('offset', String(params.offset));
    if (params.limit !== null && params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.createdBys?.length) query.set('createdBys', params.createdBys.join(','));

    const qs = query.toString();
    const url = `${BASE_URL}/campaign-manager/items${qs ? `?${qs}` : ''}`;
    const response = await this.getClient().get(url);
    return response.data;
  }

  /** 2. POST /opportunity-groups — create AD_SALES group + packages */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createOpportunityGroup(data: CreateOpportunityGroupRequest): Promise<any> {
    const response = await this.getClient().post(`${BASE_URL}/opportunity-groups`, data);
    return response.data;
  }

  /** 3. PATCH /opportunity-groups/:id — update AD_SALES (composite diff) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateOpportunityGroup(id: string, data: PatchOpportunityGroupRequest): Promise<any> {
    const response = await this.getClient().patch(`${BASE_URL}/opportunity-groups/${id}`, data);
    return response.data;
  }

  /** 4. POST /opportunities — create UPSIZE */
  async createOpportunity(data: CreateOpportunityRequest): Promise<ApiUpsizeRow> {
    const response = await this.getClient().post(`${BASE_URL}/opportunities`, data);
    return response.data;
  }

  /** 5. PATCH /opportunities/:id — update UPSIZE */
  async updateOpportunity(id: string, data: PatchOpportunityRequest): Promise<ApiUpsizeRow> {
    const response = await this.getClient().patch(`${BASE_URL}/opportunities/${id}`, data);
    return response.data;
  }

  /** 6. POST /deal-assets — create DEAL */
  async createDealAsset(data: CreateDealAssetRequest): Promise<ApiDealRow> {
    const response = await this.getClient().post(`${BASE_URL}/deal-assets`, data);
    return response.data;
  }

  /** 7. PATCH /deal-assets/:id — update DEAL */
  async updateDealAsset(id: string | number, data: PatchDealAssetRequest): Promise<ApiDealRow> {
    const response = await this.getClient().patch(`${BASE_URL}/deal-assets/${id}`, data);
    return response.data;
  }

  /** 8. POST /deal-assets/upload — create DEAL with multipart file upload */
  async createDealAssetWithFiles(formData: FormData): Promise<ApiDealRow> {
    const response = await this.getClient().post(`${BASE_URL}/deal-assets/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /** 9. POST /deal-assets/:id/opportunities — link deal asset to opportunities */
  async linkDealAssetToOpportunities(dealAssetId: string | number, opportunityIds: number[]): Promise<void> {
    await this.getClient().post(`${BASE_URL}/deal-assets/${dealAssetId}/opportunities`, { opportunityIds });
  }

  /** 10. GET /opportunity-groups/:id — get AD_SALES group by ID */
  async getOpportunityGroupById(id: string): Promise<unknown> {
    const response = await this.getClient().get(`${BASE_URL}/opportunity-groups/${id}`);
    return response.data;
  }

  /** 11. GET /opportunities/:id — get UPSIZE opportunity by ID */
  async getOpportunityById(id: string): Promise<unknown> {
    const response = await this.getClient().get(`${BASE_URL}/opportunities/${id}`);
    return response.data;
  }

  /** 12. GET /deal-assets/:id — get DEAL asset by ID */
  async getDealAssetById(id: string | number): Promise<unknown> {
    const response = await this.getClient().get(`${BASE_URL}/deal-assets/${id}`);
    return response.data;
  }

  /** 13. GET /package-manager/packages — package catalog (Q20) */
  async getPackages(params?: {
    countries?: string;
    types?: string;
  }): Promise<{ data: import('./types').ApiPackageCatalogItem[] }> {
    const query = new URLSearchParams();
    if (params?.countries) query.set('countries', params.countries);
    if (params?.types) query.set('types', params.types);
    const qs = query.toString();
    const url = `${BASE_URL}/package-manager/packages${qs ? `?${qs}` : ''}`;
    const response = await this.getClient().get(url);
    return response.data;
  }

  /** 14. GET /package-manager/assets — asset details for given asset IDs (keys from ApiPackageCatalogItem.assets) */
  async getPackageAssets(assetIds: string[]): Promise<{ data: import('./types').ApiPackageAssetItem[] }> {
    const query = new URLSearchParams();
    query.set('ids', assetIds.join(','));
    const url = `${BASE_URL}/package-manager/assets?${query.toString()}`;
    const response = await this.getClient().get(url);
    return response.data;
  }

  /** 15. GET /brands — account-scoped brand list (Q21) */
  async getBrands(params?: {
    q?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: import('./types').ApiBrand[]; totalItems?: number }> {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const response = await this.getClient().get(`${BASE_URL}/brands${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  /** 16. GET /members — paginated member roster (MIC-2653) */
  async listMembers(
    params?: { search?: string; page?: number; limit?: number },
    signal?: AbortSignal
  ): Promise<ApiListMembersResponse> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    const qs = query.toString();
    // axios 0.21.x AxiosRequestConfig doesn't declare `signal`; cast to bypass the type gap.
    const response = await this.getClient().get(`${BASE_URL}/members${qs ? `?${qs}` : ''}`, { signal } as object);
    return response.data;
  }

  /** 17. GET /members/:id — single member detail (MIC-2653) */
  async getMember(merchantUserId: string): Promise<ApiMember> {
    const response = await this.getClient().get(`${BASE_URL}/members/${merchantUserId}`);
    return response.data;
  }

  /** 18. PATCH /members/:id/brand-access — update member brand grants (MIC-2653) */
  async editBrandAccess(merchantUserId: string, body: ApiEditBrandAccessBody): Promise<ApiEditBrandAccessResponse> {
    const response = await this.getClient().patch(`${BASE_URL}/members/${merchantUserId}/brand-access`, body);
    return response.data;
  }

  /** GET /baseline — baseline commission rates for a merchant (Q21) */
  async getBaseline(merchantId: string, country: string): Promise<BaselineV2Response> {
    const response = await this.getClient().get(`${BASE_URL}/baseline`, {
      params: { merchantId, country },
    });
    return response.data;
  }

  /** 17. GET /campaigns — merchant-facing joinable campaign picker */
  async getCampaigns(params?: {
    q?: string;
    countries?: string;
    page?: number;
    limit?: number;
  }): Promise<import('./types').ApiMoopCampaignsResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.countries) query.set('countries', params.countries);
    if (params?.page !== null && params?.page !== undefined) query.set('page', String(params.page));
    if (params?.limit !== null && params?.limit !== undefined) query.set('limit', String(params.limit));
    const qs = query.toString();
    const url = `${BASE_URL}/campaigns${qs ? `?${qs}` : ''}`;
    const response = await this.getClient().get(url);
    return response.data;
  }

  async getInvoicingUnit(brandId: string): Promise<ApiInvoicingUnit | null> {
    const response = await this.getClient().get(`${BASE_URL}/invoicing-units?brandId=${encodeURIComponent(brandId)}`);
    return (response.data as { data: { invoicingUnit: ApiInvoicingUnit | null } }).data.invoicingUnit;
  }
}

export const campaignManagerAPI = new CampaignManagerAPI();
